/**
 * insurance-sync.js (DROP-IN)
 * Works in BOTH:
 *  - Cart drawer (#CartDrawer)
 *  - Cart page (/cart) using #CartInsuranceConfig (add this element in cart template)
 *
 * Key behaviors:
 *  - Uses line-item KEY (id) for cart/change removals (no line reindex issues)
 *  - Reduces “one beat behind” by capturing cart JSON responses from /cart/change + /cart/update
 *  - Suppresses auto sync during multi-step operations (toggle off/on)
 *  - Forces UI redraw:
 *      - Drawer: re-fetch section HTML and replace drawer innerHTML
 *      - Cart page: delayed reload + guaranteed post-reload sync (no manual refresh needed)
 */

(() => {
  const drawer = document.getElementById('CartDrawer');
  const configEl = drawer || document.getElementById('CartInsuranceConfig');
  if (!configEl) return;

  // Drawer-only (needed to re-render the drawer section)
  const sectionId = drawer?.dataset?.sectionId || null;

  // Config (drawer OR cart page config element)
  const insuranceVariantId = configEl.dataset.insuranceVariantId;
  const insuranceProductId = configEl.dataset.insuranceProductId;
  const stepCents = parseInt(configEl.dataset.insuranceStepCents || '0', 10);
  const signatureVariantId = configEl.dataset.signatureVariantId || null;
  const signatureMin = parseInt(configEl.dataset.signatureMin || '1', 10);

  if (!insuranceVariantId || !stepCents) return;
  if (drawer && !sectionId) return; // drawer refresh needs section id

  const ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/';

  const isCartPage = window.location.pathname === '/cart';
  const rootEl = drawer || document;
  const smallestValidSignatureQty = () => Math.max(1, signatureMin);

  // --- Post-reload sync flag (cart page) ---
  const NEXT_SYNC_KEY = 'insurance_sync_after_reload';

  // ------------------------
  // Sync suppression helpers
  // ------------------------
  let suppressSyncUntil = 0;
  const suppressSyncFor = (ms = 500) => { suppressSyncUntil = Date.now() + ms; };
  const shouldSuppressSync = () => Date.now() < suppressSyncUntil;

  // Debounced sync scheduler
  let syncTimer = null;
  const requestSync = () => {
    if (shouldSuppressSync()) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncTimer = null;
      sync();
    }, 120);
  };

  // ------------------------
  // State + helpers
  // ------------------------
  let syncing = false;
  let queued = false;
  let refreshInFlight = false;
  let signatureJustRemoved = false;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Keep a "last known cart" from mutation responses to avoid stale fetchCart reads
  let lastCart = null;
  const setLastCart = (cart) => { lastCart = cart; };
  const clearLastCart = () => { lastCart = null; };

  // Delayed refresh scheduling (helps “settle” theme-side chained cart ops)
  let delayedRefreshTimer = null;

  function scheduleDelayedRefresh() {
    if (delayedRefreshTimer) clearTimeout(delayedRefreshTimer);

    delayedRefreshTimer = setTimeout(() => {
      delayedRefreshTimer = null;

      if (drawer) {
        refreshDrawerSection();
      } else if (window.location.pathname === '/cart') {
        // Ensure we run sync AFTER the reload
        sessionStorage.setItem(NEXT_SYNC_KEY, '1');
        window.location.reload();
      }
    }, 1000); // 1 second
  }

  async function fetchCart() {
    const url = `${ROOT}cart.js?ts=${Date.now()}`;
    const res = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) throw new Error('Failed to fetch cart');
    const cart = await res.json();
    setLastCart(cart);
    return cart;
  }

  async function getCart() {
    return lastCart || fetchCart();
  }

  function parseQuery(url) {
    try {
      const u = new URL(url, window.location.origin);
      return u.searchParams;
    } catch (_) {
      const q = url.split('?')[1] || '';
      return new URLSearchParams(q);
    }
  }

  // STRICT insurance detection only
  function getInsuranceLinesStrict(cart) {
    const matches = [];

    if (insuranceVariantId) {
      matches.push(...cart.items.filter(i => String(i.variant_id) === String(insuranceVariantId)));
    }
    if (insuranceProductId) {
      matches.push(...cart.items.filter(i => String(i.product_id) === String(insuranceProductId)));
    }

    const uniq = new Map();
    for (const line of matches) uniq.set(line.key, line);
    return Array.from(uniq.values());
  }

  function getLineByVariant(cart, variantId) {
    if (!variantId) return null;
    return cart.items.find((i) => String(i.variant_id) === String(variantId)) || null;
  }

  function getNonAutoSubtotalCents(cart) {
    const insuranceKeys = new Set(getInsuranceLinesStrict(cart).map((l) => l.key));
    let subtotal = 0;

    for (const item of cart.items) {
      if (insuranceKeys.has(item.key)) continue;
      if (signatureVariantId && String(item.variant_id) === String(signatureVariantId)) continue;
      subtotal += item.final_line_price;
    }

    return subtotal;
  }

  function recommendedInsuranceQty(nonAutoSubtotalCents) {
    if (nonAutoSubtotalCents <= 0) return 0;
    return Math.floor(nonAutoSubtotalCents / stepCents) + 1;
  }

  function parseManualInsuranceQty(cart) {
    const raw = cart.attributes?.insurance_manual_qty;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.floor(n);
  }

  function isOptedOut(cart) {
    return cart.attributes?.insurance_opt_out === '1';
  }

  function getSignatureWants(cart) {
    const toggle = rootEl.querySelector('[data-signature-toggle]');
    if (toggle) return !!toggle.checked;
    return (cart.attributes?.signature_opt_in || '0') === '1';
  }

  function setToggleUIFromCart(cart) {
    const toggle = rootEl.querySelector('[data-insurance-toggle]');
    if (!toggle) return;
    const optedOut = (cart.attributes?.insurance_opt_out || '0') === '1';
    toggle.checked = !optedOut;
  }

  // ------------------------
  // Cart mutation API calls
  // ------------------------
  async function cartChange(idOrLineOrKey, quantity, { isLine = false } = {}) {
    const payload = isLine
      ? { line: Number(idOrLineOrKey), quantity }
      : { id: idOrLineOrKey, quantity }; // key string supported

    const res = await fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      cache: 'no-store',
    });

    if (!res.ok) throw new Error('Failed to change cart');
    const cart = await res.json();
    setLastCart(cart);
    return cart;
  }

  async function cartChangeByKey(key, quantity) {
    // Use key directly
    return cartChange(key, quantity, { isLine: false });
  }

  async function cartAdd(variantId, quantity) {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: Number(variantId), quantity }),
      cache: 'no-store',
    });
    if (!res.ok) throw new Error('Failed to add item');

    // add.js returns line item, not full cart — fetch cart once
    await res.json().catch(() => {});
    return fetchCart();
  }

  async function cartUpdateAttributes(attributes) {
    const res = await fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
      cache: 'no-store',
    });
    if (!res.ok) throw new Error('Failed to update attributes');
    const cart = await res.json();
    setLastCart(cart);
    return cart;
  }

  // ------------------------
  // UI Refresh
  // ------------------------
  async function refreshDrawerSection() {
    if (!drawer) return;
    if (refreshInFlight) return;
    refreshInFlight = true;

    try {
      const url = `${ROOT}?section_id=${encodeURIComponent(sectionId)}&ts=${Date.now()}`;
      const res = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshDrawer = doc.querySelector('#CartDrawer');
      if (freshDrawer) {
        drawer.innerHTML = freshDrawer.innerHTML;
        drawer.removeAttribute('hidden');

        // Re-sync toggle UI based on latest cart
        const cart = await fetchCart();
        setToggleUIFromCart(cart);
      }
    } finally {
      refreshInFlight = false;
    }
  }

  async function refreshUI() {
    if (drawer) return refreshDrawerSection();

    // On /cart: do NOT reload immediately — rely on scheduleDelayedRefresh()
    if (isCartPage) return;
  }

  // ------------------------
  // Removal helpers
  // ------------------------
  async function removeInsuranceFromCart() {
    const cart = await getCart();
    const lines = getInsuranceLinesStrict(cart);
    let changed = false;

    for (const item of lines) {
      await cartChangeByKey(item.key, 0);
      changed = true;
    }
    return changed;
  }

  async function removeAllInsuranceLines() {
    const cart = await getCart();
    const lines = getInsuranceLinesStrict(cart);
    if (!lines.length) return false;

    for (const item of lines) {
      await cartChangeByKey(item.key, 0);
    }
    return true;
  }

  // Detect insurance removal via href (/cart/change?...quantity=0)
  async function isInsuranceRemovalUrl(url) {
    try {
      const params = parseQuery(url);
      const qty = params.get('quantity');
      if (qty !== '0') return false;

      const cart = await getCart();

      const lineStr = params.get('line');
      if (lineStr) {
        const n = parseInt(lineStr, 10);
        if (Number.isFinite(n) && n >= 1 && n <= cart.items.length) {
          const item = cart.items[n - 1];
          return !!(item && (
            String(item.variant_id) === String(insuranceVariantId) ||
            (insuranceProductId && String(item.product_id) === String(insuranceProductId))
          ));
        }
      }

      const id = params.get('id');
      if (id) {
        const item = cart.items.find(i => String(i.key) === String(id));
        return !!(item && (
          String(item.variant_id) === String(insuranceVariantId) ||
          (insuranceProductId && String(item.product_id) === String(insuranceProductId))
        ));
      }

      return false;
    } catch (_) {
      return false;
    }
  }

  // ------------------------
  // Core sync
  // ------------------------
  async function sync() {
    if (syncing) { queued = true; return; }
    syncing = true;

    try {
      let cart = await getCart();
      setToggleUIFromCart(cart);

      const nonAutoSubtotal = getNonAutoSubtotalCents(cart);
      const hasRealItems = nonAutoSubtotal > 0;
      const sigLine = signatureVariantId ? getLineByVariant(cart, signatureVariantId) : null;

      let changed = false;

      // If cart has no "real" items => remove add-ons + clear attributes
      if (!hasRealItems) {
        if (await removeAllInsuranceLines()) changed = true;

        if (signatureVariantId && sigLine) {
          await cartChangeByKey(sigLine.key, 0);
          changed = true;
        }

        await cartUpdateAttributes({
          insurance_opt_out: '0',
          insurance_manual_qty: '',
          signature_opt_in: '0',
        });
        changed = true;

        if (changed) await refreshUI();
        return;
      }

      // Insurance logic
      const optedOut = isOptedOut(cart);
      const manualQty = parseManualInsuranceQty(cart);
      const insuranceLines = getInsuranceLinesStrict(cart);
      const insLine = insuranceLines[0] || null;

      if (optedOut) {
        if (await removeAllInsuranceLines()) changed = true;

      } else if (manualQty != null) {
        if (manualQty <= 0) {
          if (await removeAllInsuranceLines()) changed = true;
          await cartUpdateAttributes({ insurance_opt_out: '1', insurance_manual_qty: '0' });
          changed = true;

        } else {
          if (insuranceLines.length > 1) {
            if (await removeAllInsuranceLines()) changed = true;
          }

          if (!insLine) {
            await cartAdd(insuranceVariantId, manualQty);
            changed = true;
          } else if (insLine.quantity !== manualQty) {
            await cartChangeByKey(insLine.key, manualQty);
            changed = true;
          }
        }

      } else {
        const desired = recommendedInsuranceQty(nonAutoSubtotal);

        if (desired > 0) {
          if (insuranceLines.length > 1) {
            if (await removeAllInsuranceLines()) changed = true;
          }

          if (!insLine) {
            await cartAdd(insuranceVariantId, desired);
            changed = true;
          } else if (insLine.quantity !== desired) {
            await cartChangeByKey(insLine.key, desired);
            changed = true;
          }
        } else {
          if (await removeAllInsuranceLines()) changed = true;
        }
      }

      // Signature logic (independent of insurance)
      if (signatureVariantId) {
        if (changed) cart = await fetchCart();

        const wantsSignature = signatureJustRemoved ? false : getSignatureWants(cart);
        const desiredAttr = wantsSignature ? '1' : '0';

        if ((cart.attributes?.signature_opt_in || '0') !== desiredAttr) {
          await cartUpdateAttributes({ signature_opt_in: desiredAttr });
          changed = true;
          cart = await fetchCart();
        }

        const sigLineAfter = getLineByVariant(cart, signatureVariantId);

        if (wantsSignature) {
          const desiredQty = smallestValidSignatureQty();
          if (!sigLineAfter) { await cartAdd(signatureVariantId, desiredQty); changed = true; }
          else if (sigLineAfter.quantity !== desiredQty) { await cartChangeByKey(sigLineAfter.key, desiredQty); changed = true; }
        } else {
          if (sigLineAfter) { await cartChangeByKey(sigLineAfter.key, 0); changed = true; }
        }
      }

      if (changed) {
        scheduleDelayedRefresh(); // settle + cart page reload
        await refreshUI();        // drawer refreshes now
      }
    } catch (e) {
      console.warn('[Insurance/Signature]', e);
      clearLastCart();
    } finally {
      syncing = false;
      if (queued) {
        queued = false;
        await sleep(140);
        sync();
      }
    }
  }

  // ------------------------
  // Event handlers
  // ------------------------

  // Toggle OFF/ON (insurance)
  rootEl.addEventListener('change', (e) => {
    const toggle = e.target.closest('[data-insurance-toggle]');
    if (!toggle) return;

    const wantsOn = !!toggle.checked;

    if (wantsOn) {
      suppressSyncFor(900);

      Promise.resolve()
        .then(() => cartUpdateAttributes({ insurance_opt_out: '0', insurance_manual_qty: '' }))
        .then(() => { scheduleDelayedRefresh(); })
        .then(() => sync())
        .catch(() => sync());
    } else {
      suppressSyncFor(1600);

      Promise.resolve()
        .then(() => cartUpdateAttributes({ insurance_opt_out: '1', insurance_manual_qty: '0' }))
        .then(() => removeInsuranceFromCart())
        .then(() => { scheduleDelayedRefresh(); return refreshUI(); })
        .then(() => sync())
        .catch(() => sync());
    }
  });

  // Signature toggle
  rootEl.addEventListener('change', (e) => {
    const toggle = e.target.closest('[data-signature-toggle]');
    if (!toggle || !signatureVariantId) return;
    signatureJustRemoved = false;
    sync();
  });

  // Signature remove link protection (drawer/cart if markup matches)
  rootEl.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="/cart/change"]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    if (!href.includes('quantity=0')) return;

    const itemRoot = link.closest('line-item');
    if (!itemRoot) return;

    const isSignature = itemRoot.dataset.isSignature === 'true';
    if (!isSignature) return;

    e.preventDefault();
    signatureJustRemoved = true;

    rootEl.querySelectorAll('[data-signature-toggle]').forEach((cb) => { cb.checked = false; });

    Promise.resolve()
      .then(() => cartUpdateAttributes({ signature_opt_in: '0' }))
      .then(async () => {
        const cart = await fetchCart();
        const sig = getLineByVariant(cart, signatureVariantId);
        if (sig) await cartChangeByKey(sig.key, 0);
      })
      .then(() => refreshUI())
      .catch(async () => {
        setTimeout(() => { signatureJustRemoved = false; }, 250);
        await sync();
      });
  });

  // Insurance remove link protection: manual remove locks opt-out so it won't re-add
  rootEl.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="/cart/change"]');
    if (!link) return;

    const href = link.getAttribute('href') || '';
    if (!href.includes('quantity=0')) return;

    Promise.resolve()
      .then(async () => {
        const isInsuranceRemove = await isInsuranceRemovalUrl(href);
        if (!isInsuranceRemove) return false;

        e.preventDefault();
        suppressSyncFor(1400);

        rootEl.querySelectorAll('[data-insurance-toggle]').forEach((cb) => { cb.checked = false; });

        await cartUpdateAttributes({ insurance_opt_out: '1', insurance_manual_qty: '0' });
        await removeInsuranceFromCart();
        scheduleDelayedRefresh();
        await refreshUI();
        return true;
      })
      .catch(() => {});
  });

  // Run when drawer opens
  if (drawer) {
    const mo = new MutationObserver(() => {
      if (!drawer.hasAttribute('hidden')) sync();
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['hidden'] });
  }

  // ------------------------
  // Fetch wrapper: capture mutation responses to avoid "one beat behind"
  // ------------------------
  const originalFetch = window.fetch;
  if (!window.__insuranceSignatureFetchWrapped) {
    window.__insuranceSignatureFetchWrapped = true;

    window.fetch = async function (...args) {
      const res = await originalFetch.apply(this, args);

      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');

        if (url.includes('/cart/change') || url.includes('/cart/update')) {
          res.clone().json().then((cart) => {
            setLastCart(cart);
            requestSync();
            scheduleDelayedRefresh();
          }).catch(() => {
            requestSync();
            scheduleDelayedRefresh();
          });

        } else if (url.includes('/cart/add')) {
          setTimeout(async () => {
            try { await fetchCart(); } catch (_) {}
            requestSync();
            scheduleDelayedRefresh();
          }, 140);
        }
      } catch (_) {}

      return res;
    };
  }

  // ------------------------
  // Boot
  // ------------------------
  (async () => {
    try {
      const needsPostReloadSync = sessionStorage.getItem(NEXT_SYNC_KEY) === '1';
      if (needsPostReloadSync) sessionStorage.removeItem(NEXT_SYNC_KEY);

      const cart = await fetchCart();

      // If we reloaded specifically to settle the cart page, run one more sync after a short delay
      if (needsPostReloadSync) {
        setTimeout(() => sync(), 600);
        return;
      }

      // Respect explicit opt-out
      if ((cart.attributes?.insurance_opt_out || '0') === '1') {
        setToggleUIFromCart(cart);
        return sync();
      }

      // If no manual qty and no insurance line, allow auto-calc and add
      const manual = cart.attributes?.insurance_manual_qty;
      const hasManual = (manual != null && manual !== '');
      const insLines = getInsuranceLinesStrict(cart);

      if (!hasManual && insLines.length === 0) {
        await cartUpdateAttributes({ insurance_opt_out: '0', insurance_manual_qty: '' });
      }

      await sync();
    } catch (_) {
      clearLastCart();
      sync();
    }
  })();
})();